import { useEffect, useState } from "react";
import {
  filterNavItemByPermission,
  getGeneralNavItems,
  getHyroNavItems,
  getAccountNavItems,
  getAdminNavItems
} from "src/layout/Navigation/navigationItems";
import { NAVIGATION_MODES } from "src/layout/Navigation/NavigationBar.consts";
import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import { hasPermissionSelector, isOutboundAgentSelector, selectedAccountIdSelector } from "src/stores/selectors/appSelectors";
import useAppStore from "src/stores/appStore";
import { useStatusBarDisplayed } from "@hooks";
import { useMediaQuery, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import { CAMPAIGN_MANAGER, BRANCH_DEPLOYER } from "@consts";
import { isNotCodefullAgent, isProd } from "@utils";

const NAV_OPEN_STATE_KEY = "navigationOpenState";

const getNavigationOpenState = () => {
  const savedState = localStorage.getItem(NAV_OPEN_STATE_KEY);
  if (savedState === null || savedState === "undefined") {
    return true;
  }
  return JSON.parse(savedState);
};

const useNavigationProps = (mode = NAVIGATION_MODES.ASSISTANT) => {
  const { assistantId } = useParams();
  const [open, setOpen] = useState(getNavigationOpenState());
  const theme = useTheme();
  const accountId = useAppStore(selectedAccountIdSelector);
  const isOutboundAgent = useAppStore(isOutboundAgentSelector);
  const hasPermission = useAppStore(hasPermissionSelector);
  const showStatusBar = useStatusBarDisplayed();
  const isMediumSizeDevice = useMediaQuery(theme.breakpoints.between("xs", "md"));
  const filterNavItemByAccountPermission = filterNavItemByPermission(hasPermission);
  const filterNavItemByAgentType = (navItem) => !navItem.isCodelessRoute ||
    (navItem.isCodelessRoute && isNotCodefullAgent(assistantId));
  const filterNavItemByCampaignManager = (navItem) =>
    navItem.key !== CAMPAIGN_MANAGER || (isOutboundAgent);

  let generalNavItems = [];
  let hyroNavItems = null;
  let showAssistantSelector = false;
  const secondaryNavTitle = "Hyro Tools";

  if (mode === NAVIGATION_MODES.ASSISTANT && assistantId) {
    generalNavItems = getGeneralNavItems(accountId, assistantId)
      .filter(filterNavItemByAccountPermission)
      .filter(filterNavItemByAgentType)
      .filter(filterNavItemByCampaignManager);

    hyroNavItems = hasPermission(USER_PERMISSIONS.HYRO_TOOLS_SECTION.READ)
      && getHyroNavItems(accountId, assistantId)
        .filter(filterNavItemByAccountPermission)
        .filter(filterNavItemByAgentType);

    showAssistantSelector = true;
  } else if (mode === NAVIGATION_MODES.ACCOUNT && accountId) {
    generalNavItems = getAccountNavItems(accountId)
      .filter(filterNavItemByAccountPermission);
  } else if (mode === NAVIGATION_MODES.ADMIN) {
    generalNavItems = getAdminNavItems()
      .filter(filterNavItemByAccountPermission).filter((item) => item.key !== BRANCH_DEPLOYER || (!isProd() && item.key === BRANCH_DEPLOYER));
  }

  useEffect(() => {
    if (isMediumSizeDevice) {
      setOpen(false);
    } else {
      setOpen(getNavigationOpenState());
    }
  }, [isMediumSizeDevice]);

  const handleSetOpen = (value) => {
    setOpen(value);
    localStorage.setItem(NAV_OPEN_STATE_KEY, JSON.stringify(value));
  };

  return {
    generalNavItems,
    hyroNavItems,
    open,
    setOpen: handleSetOpen,
    showStatusBar,
    mode,
    showAssistantSelector,
    secondaryNavTitle,
    showMinimizeButton: !isMediumSizeDevice
  };
};

export default useNavigationProps;
